import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PackageOpen } from 'lucide-react';
import { usePrestamos } from '../../context/PrestamosContext';
import { usePerfil } from '../../context/PerfilContext';
import { Prestamo } from '../../types';
import {
  fraseVencido, posponerPrestamo, prestamoPospuestoHasta, prestamosVencidos,
} from '../../utils/prestamosVencidos';
import { vibrar } from '../../utils/hapticos';

/** Cuántos se enseñan como mucho; el resto, con un enlace a Préstamos. */
const MAXIMO = 3;

/** Préstamos pasados de fecha que no se han pospuesto en este dispositivo. */
function useVencidos(): { lista: Prestamo[]; refrescar: () => void } {
  const { tieneModulo } = usePerfil();
  const { prestamos } = usePrestamos();
  // Posponer vive en localStorage: este contador obliga a recalcular
  const [vuelta, setVuelta] = useState(0);
  const lista = useMemo(
    () => (tieneModulo('prestamos') ? prestamosVencidos(prestamos) : [])
      .filter(p => prestamoPospuestoHasta(p.id) < Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [prestamos, tieneModulo, vuelta],
  );
  return { lista, refrescar: () => setVuelta(v => v + 1) };
}

/**
 * Para el contador de la tarjeta de Préstamos en el Inicio: todos los que
 * están pasados de fecha, también los pospuestos (siguen sin volver).
 */
export const useNumeroPrestamosVencidos = () => {
  const { tieneModulo } = usePerfil();
  const { prestamos } = usePrestamos();
  return useMemo(
    () => (tieneModulo('prestamos') ? prestamosVencidos(prestamos).length : 0),
    [prestamos, tieneModulo],
  );
};

/**
 * Aviso al entrar (Inicio) de lo que tenía que haber vuelto y sigue fuera,
 * o de lo que tú tenías que devolver. Sin vencidos no pinta nada.
 */
export const AvisosPrestamos: React.FC = () => {
  const { lista, refrescar } = useVencidos();
  const { marcarDevuelto } = usePrestamos();
  const [guardando, setGuardando] = useState<string | null>(null);
  if (lista.length === 0) return null;

  const devuelto = async (p: Prestamo) => {
    setGuardando(p.id);
    try {
      await marcarDevuelto(p.id, new Date());
      vibrar.exito();
    } catch {
      vibrar.error();
    } finally {
      setGuardando(null);
    }
  };

  const resto = lista.length - MAXIMO;
  return (
    <div className="space-y-2 mb-5">
      {lista.slice(0, MAXIMO).map(p => (
        <div key={p.id}
          className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-xl text-sm bg-amber-50 dark:bg-amber-900/20
                     border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-200">
          <PackageOpen className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="flex-1 min-w-0">{fraseVencido(p)}</span>
          <span className="shrink-0 flex items-center gap-3">
            <button onClick={() => devuelto(p)} disabled={guardando === p.id}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60">
              {guardando === p.id ? 'Guardando…' : 'Ya ha vuelto'}
            </button>
            <button onClick={() => { posponerPrestamo(p.id); refrescar(); }}
              className="text-xs font-semibold hover:underline">
              Más tarde
            </button>
          </span>
        </div>
      ))}
      {resto > 0 && (
        <Link to="/prestamos" className="block text-sm font-semibold text-amber-800 dark:text-amber-300 hover:underline px-1">
          Y {resto === 1 ? 'otro préstamo pasado' : `${resto} préstamos pasados`} de fecha más →
        </Link>
      )}
    </div>
  );
};
